/**
 * Support Chat Service
 * API integration for the support chat (widget do usuário ↔ Central de Atendimento do Adm)
 *
 * @version 2025.1.0
 */
import apiClient from './apiClient';

export type SupportConversationSubject = 'tecnico' | 'financeiro' | 'duvida';
export type SupportConversationStatus = 'open' | 'resolved';
export type SupportFeedbackTag = 'resolveu' | 'rapido' | 'educado' | 'demorou' | 'nao_resolveu';

export interface SupportConversationUser {
  id: number;
  name: string;
  email: string;
  whatsapp: string | null;
}

export interface SupportConversationCompany {
  id: number;
  legalName: string;
  tradeName: string | null;
}

export interface SupportConversationAssignee {
  id: number;
  name: string;
}

export interface SupportConversation {
  id: number;
  companyId: number;
  userId: number;
  subject: SupportConversationSubject | null;
  status: SupportConversationStatus;
  resolvedAt: string | null;
  feedbackTags: SupportFeedbackTag[] | null;
  ratedAt: string | null;
  /** `null` enquanto ninguém do time aceitou — a conversa está na fila. */
  assignedToId: number | null;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: SupportConversationUser;
  assignedTo?: SupportConversationAssignee | null;
  company?: SupportConversationCompany;
}

export interface SupportMessage {
  id: number;
  conversationId: number;
  senderId: number;
  text: string;
  createdAt: string;
}

export interface SupportTeamMemberStats {
  id: number;
  name: string;
  repliedCount: number;
  /** Conversas abertas atribuídas a essa pessoa agora — carga de trabalho atual. */
  activeCount: number;
  ratedCount: number;
  /** `null` enquanto essa pessoa não tem nenhuma conversa avaliada ainda. */
  positivePercent: number | null;
  feedbackPercentages: Record<SupportFeedbackTag, number>;
}

export interface SupportStats {
  openCount: number;
  awaitingCount: number;
  /** Abertas e ainda sem ninguém ter aceitado. */
  queueCount: number;
  /** Na fila há mais que o limite de espera longa. */
  longWaitCount: number;
  /** Minutos até alguém aceitar, em média. */
  avgWaitMinutes: number | null;
  avgResponseMinutes: number | null;
  ratedCount: number;
  feedbackPercentages: Record<SupportFeedbackTag, number>;
  team: SupportTeamMemberStats[];
}

interface ConversationWithMessages {
  conversation: SupportConversation;
  messages: SupportMessage[];
}

interface OwnConversationWithMessages {
  /** `null` só quando a pessoa nunca mandou mensagem nenhuma. */
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  responsibleName: string | null;
}

class SupportChatService {
  private baseUrl = '/support/conversations';

  /** Widget: própria conversa do usuário logado (abre uma se ainda não existir). */
  async getOwnConversation(): Promise<OwnConversationWithMessages> {
    const response = (await apiClient.get(`${this.baseUrl}/me`)) as { data: OwnConversationWithMessages };
    return response.data;
  }

  async sendOwnMessage(text: string, subject?: SupportConversationSubject): Promise<SupportMessage> {
    const response = (await apiClient.post(`${this.baseUrl}/me/messages`, { text, subject })) as {
      data: SupportMessage;
    };
    return response.data;
  }

  /** Quantas mensagens de usuários ainda não foram vistas pelo Adm — para o balão da navbar. */
  async getUnreadCount(): Promise<number> {
    const response = (await apiClient.get(`${this.baseUrl}/unread-count`)) as { data: { count: number } };
    return response.data.count;
  }

  /** Painel de entrada da Central de Atendimento: números, avaliações e time. */
  async getStats(): Promise<SupportStats> {
    const response = (await apiClient.get(`${this.baseUrl}/stats`)) as { data: SupportStats };
    return response.data;
  }

  /** Central de Atendimento: conversas da empresa ativa. */
  async listConversations(status?: SupportConversationStatus): Promise<SupportConversation[]> {
    const query = status ? `?status=${status}` : '';
    const response = (await apiClient.get(`${this.baseUrl}${query}`)) as { data: SupportConversation[] };
    return response.data;
  }

  async getConversationMessages(id: number): Promise<ConversationWithMessages> {
    const response = (await apiClient.get(`${this.baseUrl}/${id}/messages`)) as {
      data: ConversationWithMessages;
    };
    return response.data;
  }

  /** Aceita uma conversa da fila — a partir daqui, só quem aceitou pode responder. */
  async acceptConversation(id: number): Promise<SupportConversation> {
    const response = (await apiClient.post(`${this.baseUrl}/${id}/accept`, {})) as {
      data: SupportConversation;
    };
    return response.data;
  }

  async replyToConversation(id: number, text: string): Promise<SupportMessage> {
    const response = (await apiClient.post(`${this.baseUrl}/${id}/messages`, { text })) as {
      data: SupportMessage;
    };
    return response.data;
  }

  /** Widget: usuário marca o que descreveu o atendimento já encerrado — multiescolha. */
  async submitFeedback(id: number, tags: SupportFeedbackTag[]): Promise<SupportConversation> {
    const response = (await apiClient.post(`${this.baseUrl}/${id}/feedback`, { tags })) as {
      data: SupportConversation;
    };
    return response.data;
  }

  async updateConversationStatus(
    id: number,
    status: SupportConversationStatus
  ): Promise<SupportConversation> {
    const response = (await apiClient.patch(`${this.baseUrl}/${id}`, { status })) as {
      data: SupportConversation;
    };
    return response.data;
  }
}

export const supportChatService = new SupportChatService();
export default supportChatService;
